/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\ArticleLibreModal.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT MODAL ARTICLE LIBRE

📋 DESCRIPTION:
Composant React modal pour l'ajout d'articles libres au devis.
Interface de saisie rapide pour articles personnalisés (matières, main d'œuvre, sous-traitance).

🎯 RESPONSABILITÉS:
- Interface modale pour saisie d'articles libres personnalisés
- Validation des données saisies (nom, montant, type)
- Gestion des types d'articles (matière, main d'œuvre, sous-traitance)
- Calcul automatique des marges fournisseurs selon le type
- Création d'objets compatibles avec l'interface Article
- Transmission des données au composant parent

🔗 DÉPENDANCES:
- React: Hooks useState pour gestion d'état du formulaire
- types.ts: Interface Article pour compatibilité
- CSS: Classes utilitaires pour interface modale

📡 INTERACTIONS:
- Parent: Réception callbacks onClose et onSave
- Formulaire: Validation en temps réel des saisies
- Interface: Gestion affichage/masquage modal
- Données: Création articles compatibles avec système

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Support 3 types d'articles libres (matière, MO, sous-traitance)
- Gestion automatique des marges fournisseurs pour calcul MB
- Validation formulaire avec messages d'erreur contextuels
- Réinitialisation automatique après ajout
- Interface responsive avec design cohérent
- Génération ID unique avec timestamp pour éviter conflits
*/
import React, { useState, ChangeEvent, FormEvent } from 'react';

interface ArticleLibreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (article: any) => void;
  initialArticle?: any;
  mode?: 'create' | 'edit';
}

const ArticleLibreModal: React.FC<ArticleLibreModalProps> = ({ isOpen, onClose, onSave, initialArticle, mode = 'create' }) => {
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [montant, setMontant] = useState('');
  const [type, setType] = useState('matiere'); // 'matiere', 'main_oeuvre' ou 'sous_traitance'
  const [marge, setMarge] = useState('30'); // Marge fournisseur en pourcentage
  const [error, setError] = useState<string | null>(null);

  // Pré-remplir le formulaire en mode édition
  React.useEffect(() => {
    if (initialArticle) {
      setNom(initialArticle.nom || '');
      setDescription(initialArticle.nomTechnique || '');
      setMontant(String(initialArticle.prixUnitaire || initialArticle.prix || ''));
      setType(initialArticle.typeArticleLibre || 'matiere');
      setMarge(initialArticle.margeFournisseur !== undefined ? String(initialArticle.margeFournisseur) : '30');
    } else {
      setNom('');
      setDescription('');
      setMontant('');
      setType('matiere');
      setMarge('30');
    }
    setError(null);
  }, [initialArticle, isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!nom.trim()) {
      setError('Le nom est obligatoire');
      return;
    }
    
    if (!montant.trim() || isNaN(parseFloat(montant))) {
      setError('Veuillez saisir un montant valide');
      return;
    }
    
    // Créer l'article libre
    const articleLibre = {
      id: initialArticle?.id || `article-libre-${Date.now()}`,
      nom,
      nomTechnique: description || 'Article libre',
      type: 'article_libre',
      quantite: 1,
      prixUnitaire: parseFloat(montant),
      estArticleLibre: true,
      typeArticleLibre: type,
      margeFournisseur: (type === 'matiere' || type === 'sous_traitance') ? parseFloat(marge) : undefined,
      // Ajouter des propriétés minimales pour être compatible avec l'interface Article
      forme: 'libre',
      dimensions: {},
      options: {},
      materiaux: {
        dessus: { matiere: '', couleur: '' },
        dessous: { matiere: '', couleur: '' },
        cotes: { matiere: '', couleur: '' },
      },
    };
    
    // Envoyer l'article au parent
    onSave(articleLibre);
    
    // Réinitialiser le formulaire
    setNom('');
    setDescription('');
    setMontant('');
    setType('matiere');
    setError(null);
    
    // Fermer le modal
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Modifier un article libre' : 'Ajouter un article libre'}
          </h2>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <span className="sr-only">Fermer</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
              Nom *
            </label>
            <input
              type="text"
              id="nom"
              className="input-field w-full"
              value={nom}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNom(e.target.value)}
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              className="input-field w-full"
              value={description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="montant" className="block text-sm font-medium text-gray-700 mb-1">
              Montant forfaitaire (€) *
            </label>
            <input
              type="number"
              id="montant"
              className="input-field w-full"
              value={montant}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMontant(e.target.value)}
              step="0.01"
              min="0"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <div className="space-y-2">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="type"
                  value="matiere"
                  checked={type === 'matiere'}
                  onChange={() => setType('matiere')}
                />
                <span className="ml-2">Marchandise/Matière</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="type"
                  value="main_oeuvre"
                  checked={type === 'main_oeuvre'}
                  onChange={() => setType('main_oeuvre')}
                />
                <span className="ml-2">Main d'œuvre</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="type"
                  value="sous_traitance"
                  checked={type === 'sous_traitance'}
                  onChange={() => setType('sous_traitance')}
                />
                <span className="ml-2">Sous-traitance</span>
              </label>
            </div>
          </div>

          {/* Champ marge fournisseur - visible pour matiere et sous_traitance */}
          {(type === 'matiere' || type === 'sous_traitance') && (
            <div className="mb-6">
              <label htmlFor="marge" className="block text-sm font-medium text-gray-700 mb-1">
                Remise accordée par le fournisseur (%)
              </label>
              <input
                type="number"
                id="marge"
                className="input-field w-full"
                value={marge}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setMarge(e.target.value)}
                step="1"
                min="0"
                max="100"
                placeholder="30"
              />
              <small className="text-gray-500 mt-1 block">
                Pourcentage de remise accordée par le fournisseur (utilisé pour calculer la marge brute)
              </small>
            </div>
          )}
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
            >
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {mode === 'edit' ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticleLibreModal;
